import {
  Fragment,
  useState,
  useEffect,
  SyntheticEvent,
  useRef,
  SetStateAction,
} from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { FieldProps } from "@/core/components/Field";
import { Controller, useFormContext } from "react-hook-form";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import getVariableFromString from "@/core/utils/getVariableFromString";
import { useGetPreviousAnswers } from "@/core/hooks/useGetPreviousAnswers/useGetPreviousAnswers";
import { useFieldError } from "@/core/hooks/useFieldError/useFieldError";
import { IconComponent } from "@/core/components/IconComponent/IconComponent";
import { useTheme, Theme, FilterOptionsState } from "@mui/material";
import { LoadingText } from "@/core/components/Autosuggest/LoadingText";
import isIOS from "@/utils/isIOS";
import smoothScroll from "@/utils/smothScroll";
import { AddressData, AirlineData, AirportData } from "@/core/types/types";

export enum AutosuggestInputSource {
  AIRPORTS = "airports",
  AIRLINES = "airlines",
  ADDRESSES = "addresses",
}

export type AutosuggestDataFetcher = (
  input: string
) => Promise<AutosuggestListOption[]>;

export type AutosuggestListOption = AirlineData | AirportData | AddressData;

interface AutosuggestFieldProps extends FieldProps {
  initialValue?: AutosuggestListOption;
  minLength?: number;
  loadingText?: string;
  getData: AutosuggestDataFetcher;
  getInputFromOption: (option: AutosuggestListOption) => string | undefined;
  filterOptions?: (
    options: AutosuggestListOption[],
    autosuggestInput: string
  ) => AutosuggestListOption[];
  isOptionEqualToValue: (
    option: AutosuggestListOption,
    autosuggestInput: string
  ) => boolean;
  getLabelForOption: (option: AutosuggestListOption) => string;
  handleDefaultValue: ({
    currentDefaultValue,
    setInputValue,
    setAutosuggestInput,
    setValue,
  }: {
    currentDefaultValue: AutosuggestListOption | string;
    setInputValue: (value: SetStateAction<string>) => void;
    setAutosuggestInput: (input: string) => void;
    setValue: (fieldKey: string, option: AutosuggestListOption) => void;
  }) => void;
}

const getDataForAutosuggest = async (
  autosuggestInput: string,
  getData: AutosuggestDataFetcher,
  setLoading: (loading: boolean) => void
): Promise<AutosuggestListOption[] | undefined> => {
  try {
    setLoading(true);
    return getData(autosuggestInput);
  } finally {
    setLoading(false);
  }
};

const Autosuggest = ({
  field,
  initialValue,
  minLength = 0,
  loadingText,
  getData,
  getInputFromOption,
  filterOptions = (options: AutosuggestListOption[]) => options,
  isOptionEqualToValue,
  getLabelForOption,
  handleDefaultValue,
}: AutosuggestFieldProps) => {
  const theme: Theme = useTheme<Theme>();
  const [open, setOpen] = useState<boolean>(false);
  const [autosuggestInput, setAutosuggestInput] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [options, setOptions] = useState<readonly AutosuggestListOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const isOptionsEmpty: boolean = open && options?.length === 0;
  const disabled: boolean | undefined = field.disabled;
  const {
    setValue,
    control,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const textFieldRef = useRef<HTMLDivElement | null>(null);
  const autocompleteRef = useRef<HTMLDivElement | null>(null);
  const { defaultValue } = field;
  const defaultCamundaValue =
    defaultValue && getVariableFromString(defaultValue);
  const { result } = useGetPreviousAnswers(defaultCamundaValue, {
    dataIsRequired: false,
  });
  const currentDefaultValue =
    (defaultCamundaValue && result?.[defaultCamundaValue]) ||
    defaultCamundaValue;
  const { hasError, errorMessage, errorTranslationKey } = useFieldError(
    errors,
    field
  );
  const autosuggestLoadingText = loadingText || t("common.no.options.found");
  const isTablet: boolean = window.innerWidth <= theme.breakpoints.values.md;

  useEffect(() => {
    if (initialValue) {
      setInputValue(getLabelForOption(initialValue));
    }
  }, [initialValue]);

  useEffect(() => {
    if (!open) return;

    if (autosuggestInput.length < minLength) return;

    (async () => {
      const optionsToSet = await getDataForAutosuggest(
        autosuggestInput || "a",
        getData,
        setLoading
      );
      setOptions(optionsToSet ?? []);
    })();
    highlightDynamicallyInsertedElement();
  }, [autosuggestInput, open]);

  useEffect(() => {
    handleDefaultValue({
      currentDefaultValue,
      setInputValue,
      setAutosuggestInput,
      setValue,
    });
  }, [result]);

  useEffect(() => {
    highlightDynamicallyInsertedElement();
  }, [options]);

  const highlightDynamicallyInsertedElement = () => {
    // Insure highlight logic for MuiAutocompletePopper element
    const dropdownWrapper: HTMLElement | null | undefined =
      autocompleteRef.current?.parentElement;
    const noFocusedItemFound: boolean =
      dropdownWrapper?.querySelector(".MuiAutocomplete-option.Mui-focused") ===
      null;

    if (dropdownWrapper && noFocusedItemFound) {
      const firstElement: Element | null = dropdownWrapper?.querySelector(
        ".MuiAutocomplete-option"
      );
      if (firstElement) {
        firstElement.className += " Mui-focused";
      }
    }
  };

  const handleChange = (
    _event: SyntheticEvent,
    option: AutosuggestListOption | null | string,
    reason: string
  ) => {
    if (reason === "clear" && !option) {
      setValue(field.key, "");
      setAutosuggestInput("");
      setInputValue("");
    } else {
      if (option === null || typeof option === "string") return;

      const input = getInputFromOption(option);
      if (!input) return;

      setValue(field.key, option);
      setAutosuggestInput(input);
      clearErrors(field.key);
    }
  };

  const performScroll = async (): Promise<void> => {
    if (!isTablet || !textFieldRef.current) return;

    await new Promise<void>((resolve) => setTimeout(resolve, 50));

    const textField: HTMLDivElement = textFieldRef.current as HTMLDivElement;
    const flightSummaryEditModal: HTMLElement | null =
      textField.closest(".flightSummaryEdit");
    const referenceElement: HTMLElement | Window =
      flightSummaryEditModal || window;
    const offsetTop: number =
      textField.offsetTop - (flightSummaryEditModal ? 90 : 24);

    return new Promise<void>((resolve) => {
      if (isIOS()) {
        requestAnimationFrame(() => {
          referenceElement.scrollTo({
            top: offsetTop,
            behavior: "smooth",
          });
          resolve();
        });
      } else {
        smoothScroll(offsetTop, 500, referenceElement);
        resolve();
      }
    });
  };

  const handleMouseDown = () => {
    !autosuggestInput && setAutosuggestInput("a");
    setIsFocused(true);
    performScroll();
  };

  const handleMouseUp = () => {
    const input: HTMLInputElement | null | undefined =
      textFieldRef.current?.querySelector("input");
    if (input) {
      input.focus();
      input.select();
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const hasMinimumLength: boolean = autosuggestInput.length >= 2;

    if (!hasMinimumLength) {
      setAutosuggestInput("");
    }

    if (!options.length) {
      setValue(field.key, "");
      setAutosuggestInput("");
      setInputValue("");
    }
  };

  const filterAutosuggestOptions = (
    options: AutosuggestListOption[],
    autosuggestInput: FilterOptionsState<AutosuggestListOption>
  ) => {
    const input = autosuggestInput.inputValue.trim().toLowerCase();

    if (!input) {
      return options;
    }

    return filterOptions(options, input);
  };

  const getLabel = (option: AutosuggestListOption | string): string => {
    if (typeof option === "object" && option !== null) {
      return getLabelForOption(option);
    }
    return option;
  };

  return (
    <Controller
      name={field.key}
      control={control}
      rules={{
        required: {
          value: field.validate?.required || false,
          message: t(`validate.${errorTranslationKey}.required.message`),
        },
      }}
      defaultValue={initialValue}
      render={() => {
        return (
          <Autocomplete
            ref={autocompleteRef}
            autoSelect
            autoHighlight
            autoComplete
            disablePortal
            id={field.key}
            value={inputValue}
            open={open}
            options={options}
            loading={
              autosuggestInput.length < minLength
                ? false
                : loading || isOptionsEmpty
            }
            loadingText={LoadingText({
              loading,
              theme,
              text: autosuggestLoadingText,
            })}
            filterOptions={filterAutosuggestOptions}
            freeSolo={!loading}
            clearOnBlur={loading}
            disabled={disabled}
            onOpen={() => {
              setOpen(true);
              handleMouseDown();
            }}
            onClose={() => setOpen(false)}
            onClick={handleMouseDown}
            onFocus={handleMouseDown}
            onMouseUp={handleMouseUp}
            onBlur={handleBlur}
            onChange={handleChange}
            onInput={(event: React.ChangeEvent<HTMLInputElement>) =>
              setAutosuggestInput(event.target.value)
            }
            clearIcon={<HighlightOffIcon color="secondary" fontSize="small" />}
            isOptionEqualToValue={(option: AutosuggestListOption) =>
              isOptionEqualToValue(option, autosuggestInput)
            }
            getOptionLabel={getLabel}
            renderInput={(params) => (
              <TextField
                {...params}
                ref={textFieldRef}
                placeholder={
                  !isFocused && field.properties?.icon ? t(field.label) : ""
                }
                error={hasError}
                helperText={errorMessage}
                label={
                  autosuggestInput.length > 1 ||
                  isFocused ||
                  !field.properties?.icon
                    ? t(field.label)
                    : null
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: field.properties?.icon ? (
                    <div className="autosuggest-icon-wrapper">
                      <IconComponent
                        iconSrc={`images/icons/${field.properties.icon}.svg`}
                      />
                    </div>
                  ) : null,
                  endAdornment: (
                    <Fragment>
                      {loading ? (
                        <CircularProgress
                          size={15}
                          sx={{ color: theme.palette.grey[800] }}
                        />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                  className: `${params.InputProps.className} ${
                    autosuggestInput.length !== 0 ? "has-value" : "no-value"
                  }`,
                }}
              />
            )}
          />
        );
      }}
    />
  );
};

export default Autosuggest;
